import { onApiError } from "../error";
import { del, get, put } from "./client";

/**
 * @param {string} imei
 * @returns {Promise<Smartplug>}
 */
async function getSmartplug(imei) {
	const res = await get(`/smartplugs/${imei}`);

	if (res.status === 408) throw new Error("TIMEOUT");
	if (!res.ok) throw await onApiError(res);

	const plug = await res.json();
	return plug;
}

/**
 * @typedef {Object} ConsumptionDataPoint
 * @property {number} Consumption
 * @property {string} Imei
 * @property {string} Timestamp
 */

/**
 * @param {string} imei
 * @param {DataPointQueryParams} [args]
 * @returns {Promise<ConsumptionDataPoint[]|Blob>}
 */
async function getSmartplugConsumption(imei, args) {
	const queryString = [
		args?.from ? `from=${args.from}` : "",
		args?.to ? `to=${args.to}` : "",
		args?.format ? `format=${args.format}` : "",
	];

	let path = `/smartplugs/${imei}/consumption`;
	if (queryString.length > 0) {
		path += `?${queryString}`;
	}

	const res = await get(path);
	if (!res.ok) throw await onApiError(res);
	if (args?.format !== "json") return res.blob();
	return res.json();
}

/**
 * @typedef {Object} LoadDataPoint
 * @property {string} Imei
 * @property {number} Load
 * @property {string} Timestamp
 */

/**
 * @param {string} imei
 * @param {DataPointQueryParams} [args]
 * @returns {Promise<LoadDataPoint[]|Blob>}
 */
async function getSmartplugLoad(imei, args) {
	const queryString = [
		args?.from ? `from=${args.from}` : "",
		args?.to ? `to=${args.to}` : "",
		args?.format ? `format=${args.format}` : "",
	];

	let path = `/smartplugs/${imei}/load`;
	if (queryString.length > 0) {
		path += `?${queryString}`;
	}

	const res = await get(path);
	if (!res.ok) throw await onApiError(res);
	if (args?.format !== "json") return res.blob();
	return res.json();
}

/**
 * @typedef {Object} TemperatureDataPoint
 * @property {string} Imei
 * @property {number} Temperature
 * @property {string} Timestamp
 */

/**
 * @param {string} imei
 * @param {DataPointQueryParams} [args]
 * @returns {Promise<TemperatureDataPoint[]|Blob>}
 */
async function getSmartplugTemperature(imei, args) {
	const queryString = [
		args?.from ? `from=${args.from}` : "",
		args?.to ? `to=${args.to}` : "",
		args?.format ? `format=${args.format}` : "",
	];

	let path = `/smartplugs/${imei}/temperature`;
	if (queryString.length > 0) {
		path += `?${queryString}`;
	}

	const res = await get(path);

	if (!res.ok) throw await onApiError(res);
	if (args?.format !== "json") return res.blob();
	return res.json();
}

/**
 * @param {string} imei
 * @param {PutSmartplugParams} args
 * @returns {Promise<Smartplug>}
 */
async function putSmartplug(imei, args) {
	const res = await put(`/smartplugs/${imei}`, {
		body: JSON.stringify(args),
	});

	if (!res.ok) throw await onApiError(res);

	const plug = await res.json();
	return plug;
}

/**
 * @param {string} imei
 * @param {boolean} enabled
 * @returns {Promise<Smartplug>}
 */
async function putSmartplugPowerEnabled(imei, enabled) {
	const res = await put(`/smartplugs/${imei}/power-enabled`, {
		body: JSON.stringify({
			Enabled: enabled,
		}),
		timeout: 1000 * 60,
	});

	if (!res.ok) throw await onApiError(res);
	return res.json();
}

/**
 * @param {string} imei
 * @returns {Promise<Smartplug>}
 */
async function resetSmartplugConsumption(imei) {
	const res = await put(`/smartplugs/${imei}/consumption/reset`);
	if (!res.ok) throw await onApiError(res);
	return res.json();
}

/**
 * @param {string} imei
 */
async function unregisterSmartplug(imei) {
	const res = await del(`/smartplugs/${imei}`);
	if (!res.ok) throw await onApiError(res);
}

export {
	getSmartplug,
	getSmartplugConsumption,
	getSmartplugLoad,
	getSmartplugTemperature,
	putSmartplug,
	putSmartplugPowerEnabled,
	resetSmartplugConsumption,
	unregisterSmartplug,
};
